import axios from 'axios';
import { showGlobalNotification } from '../context/NotificationContext.js';
import { actAsHeader, getActAs, stopActAs } from './actAs';

/**
 * Configure axios with interceptors for handling authentication
 * This includes handling token expiration and redirecting to login
 */
const setupAxiosInterceptors = () => {
  // Response interceptor
  axios.interceptors.response.use(
    // For successful responses, just return the response
    (response) => response,
    
    // For error responses, check if it's an authentication error
    (error) => {
      // Check if the error is a 401 Unauthorized
      // An act-as session that ran out (or whose company vanished) ends that session,
      // not the owner's login: forget the token and reload as White Knight
      const code = error.response?.data?.code;
      if ((code === 'ACT_AS_EXPIRED' || code === 'ACT_AS_GONE') && getActAs()) {
        stopActAs();
        showGlobalNotification('Your act-as session has ended. You are back in your own company.', 'info');
        setTimeout(() => { window.location.href = '/owner'; }, 300);
        return Promise.reject(error);
      }

      if (error.response && error.response.status === 401) {
        const requestUrl = error.config?.url || '';

        // Endpoints whose 401s are part of their own flow, NOT a session
        // expiry: the public rate-agreement pages answer 401 + requiresAuth
        // as their normal "please verify access" handshake, and a failed
        // login/2FA attempt is a bad credential, not an expired session.
        // Treating those as expiry wiped the stored token and destroyed
        // "Remember me for 30 days" sessions.
        const isAuthFlowEndpoint =
          requestUrl.includes('/api/rate-agreement') ||
          requestUrl.includes('/api/facility-rate-agreement') ||
          requestUrl.includes('/api/auth/login') ||
          requestUrl.includes('/api/auth/verify-2fa');
        const isVerificationHandshake = error.response.data?.requiresAuth === true;
        // Requests that never carried our token can't invalidate the session.
        const hadAuthHeader = !!error.config?.headers?.Authorization;

        if (!isAuthFlowEndpoint && !isVerificationHandshake && hadAuthHeader) {
          // Clear authentication data from localStorage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          sessionStorage.removeItem('backup_token');
          sessionStorage.removeItem('backup_user');
          stopActAs();

          // Redirect to login page
          window.location.href = '/login';

          // Show a message to the user
          showGlobalNotification('Your session has expired. Please log in again.', 'error');
        }
      }

      // Return the error for further processing
      return Promise.reject(error);
    }
  );
  
  // Request interceptor to add token to all requests
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      
      // If token exists, add it to the request headers
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // A White Knight owner acting as a partner company
        Object.assign(config.headers, actAsHeader());
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
};

export default setupAxiosInterceptors;
